import { useCallback, useEffect, useMemo, useState } from 'react';

interface CodeBlockWithCopyProps {
  code: string;
  language?: string;
}

type CopyState = 'idle' | 'copied' | 'error';

const languageLabels: Record<string, string> = {
  bash: 'Bash',
  sh: 'Shell',
  json: 'JSON',
  http: 'HTTP',
  javascript: 'JavaScript',
  js: 'JavaScript',
  typescript: 'TypeScript',
  ts: 'TypeScript',
  python: 'Python',
  go: 'Go',
  text: 'Text',
};

export function CodeBlockWithCopy({ code, language }: CodeBlockWithCopyProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');

  const label = useMemo(() => {
    if (copyState === 'copied') return '已复制';
    if (copyState === 'error') return '失败';
    return '复制';
  }, [copyState]);

  const langLabel = language ? (languageLabels[language] || language.toUpperCase()) : null;

  useEffect(() => {
    if (copyState === 'idle') return;
    const t = window.setTimeout(() => setCopyState('idle'), 1500);
    return () => window.clearTimeout(t);
  }, [copyState]);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
        setCopyState('copied');
        return;
      }
      fallbackCopy(code);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  }, [code]);

  return (
    <div className="relative my-4 group">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-800 dark:bg-zinc-800 rounded-t-xl border-b border-slate-700 dark:border-zinc-700">
        <div className="flex items-center gap-2">
          {/* Terminal dots */}
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500/80" />
            <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
            <span className="w-3 h-3 rounded-full bg-green-500/80" />
          </div>
          {langLabel && (
            <span className="ml-3 text-xs font-medium text-slate-400 dark:text-zinc-500">
              {langLabel}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className={[
            'flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md transition-all duration-150',
            copyState === 'copied'
              ? 'bg-green-500/20 text-green-400'
              : copyState === 'error'
                ? 'bg-red-500/20 text-red-400'
                : 'bg-slate-700 dark:bg-zinc-700 text-slate-300 dark:text-zinc-400 hover:bg-slate-600 dark:hover:bg-zinc-600 hover:text-white',
          ].join(' ')}
          aria-label="copy code"
        >
          {copyState === 'copied' ? (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          )}
          {label}
        </button>
      </div>
      {/* Code content */}
      <pre className="rounded-b-xl bg-slate-900 dark:bg-zinc-900 px-4 py-4 text-[13px] leading-relaxed text-slate-100 overflow-x-auto border border-t-0 border-slate-800 dark:border-zinc-800">
        <code className={language ? `language-${language}` : undefined}>{code}</code>
      </pre>
    </div>
  );
}

function fallbackCopy(text: string) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}
